import { IconClock } from "@/assets/icons/geist/IconClock";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { ResourceUsageSkeleton } from "@/components/environments/ResourceUsageSkeleton";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import { formatBytes } from "@/format/bytes";
import { timeAgo } from "@/format/time";
import { getHealthLevel, THRESHOLD_HIGH, THRESHOLD_MEDIUM } from "@/components/environments/resource-usage-health";
import type { DiskIORate, VMSnapshot } from "@/hooks/use-vm-live-usage";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { Link } from "react-router-dom";

// --- Resource Usage Content (used by ResourceUsageDialog) ---

export type ResourceUsageContentProps = {
    data: VMSnapshot | undefined;
    diskIORate: DiskIORate | undefined;
    error: string | undefined;
    isLoading: boolean;
    lastUpdated: Date | undefined;
    onRefresh?: () => void;
    /** When set, shows a banner linking to project settings if usage is medium/high */
    projectId?: string;
};

export const ResourceUsageContent: FC<ResourceUsageContentProps> = ({
    data,
    diskIORate,
    error,
    isLoading,
    lastUpdated,
    onRefresh,
    projectId,
}) => {
    const healthLevel = getHealthLevel(data);
    const showUpgradeBanner = healthLevel === "medium" || healthLevel === "high";

    return (
        <div data-testid="resource-usage-panel">
            {data && <ResourceStats snapshot={data} diskIORate={diskIORate} />}
            {!data && !error && isLoading && <ResourceUsageSkeleton />}
            {!data && error && (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <Text className="text-base font-medium text-content-primary">
                        Couldn&apos;t load resource usage
                    </Text>
                    <Text className="text-sm text-content-secondary">
                        Try again, or contact support if the issue persists.
                    </Text>
                    {onRefresh && (
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={onRefresh}
                            disabled={isLoading}
                            data-tracking-id="resource-usage-retry"
                            LeadingIcon={(props) => (
                                <IconRefresh {...props} className={cn(props.className, isLoading && "animate-spin")} />
                            )}
                        >
                            Retry
                        </Button>
                    )}
                </div>
            )}
            <ResourceUsageFooter lastUpdated={lastUpdated} isLoading={isLoading} onRefresh={onRefresh} />
            {data && error && (
                <Banner
                    variant="danger"
                    className="mt-5"
                    text="Couldn't refresh data. Try again, or contact support if the issue persists."
                    action={
                        onRefresh
                            ? {
                                  text: "Retry",
                                  onClick: onRefresh,
                                  disabled: isLoading,
                                  "data-tracking-id": "resource-usage-retry",
                              }
                            : undefined
                    }
                />
            )}
            {showUpgradeBanner && (
                <Banner
                    variant="warning"
                    className="mt-5"
                    text="Running low on resources? Consider using a larger environment class."
                    link={
                        projectId ? (
                            <Link
                                to={`/projects/${projectId}/settings`}
                                className="whitespace-nowrap underline"
                                data-tracking-id="resource-usage-upgrade-class"
                            >
                                Project settings
                            </Link>
                        ) : undefined
                    }
                />
            )}
        </div>
    );
};

// --- Sub-components ---

type ResourceUsageFooterProps = {
    lastUpdated: Date | undefined;
    isLoading: boolean;
    onRefresh?: () => void;
};

const ResourceUsageFooter: FC<ResourceUsageFooterProps> = ({ lastUpdated, isLoading, onRefresh }) => {
    const [, setTick] = useState(0);

    // Re-render every 10s to keep the relative timestamp fresh
    useEffect(() => {
        const interval = setInterval(() => setTick((t) => t + 1), 10_000);
        return () => clearInterval(interval);
    }, []);

    if (!lastUpdated) {
        return null;
    }

    const label = `Updated ${Date.now() - lastUpdated.getTime() < 5000 ? "just now" : timeAgo(lastUpdated)}`;

    return (
        <div className="mt-5 flex items-center justify-between border-t border-border-base pt-5">
            <div className="flex items-center gap-1.5">
                <IconClock size="sm" className="text-content-tertiary" style={{ marginTop: -1 }} aria-hidden />
                <Text className="text-sm text-content-tertiary">{label}</Text>
            </div>
            {onRefresh && (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={onRefresh}
                    disabled={isLoading}
                    data-tracking-id="resource-usage-refresh"
                    LeadingIcon={(props) => (
                        <IconRefresh {...props} className={cn(props.className, isLoading && "animate-spin")} />
                    )}
                >
                    Refresh
                </Button>
            )}
        </div>
    );
};

const ResourceStats: FC<{ snapshot: VMSnapshot; diskIORate: DiskIORate | undefined }> = ({
    snapshot,
    diskIORate,
}) => {
    const { vm } = snapshot;

    const memPercent = vm.memory.total_bytes > 0 ? (vm.memory.used_bytes / vm.memory.total_bytes) * 100 : 0;
    const diskPercent = vm.disk.total_bytes > 0 ? (vm.disk.used_bytes / vm.disk.total_bytes) * 100 : 0;

    return (
        <div className="flex flex-col gap-5">
            <UsageBar label="CPU" percent={vm.cpu.usage_percent} />

            <UsageBar
                label="Memory"
                percent={memPercent}
                detail={`${formatBytes(vm.memory.used_bytes)} / ${formatBytes(vm.memory.total_bytes)}`}
            />

            <UsageBar
                label="Disk"
                percent={diskPercent}
                detail={`${formatBytes(vm.disk.used_bytes)} / ${formatBytes(vm.disk.total_bytes)}`}
            />

            <div className="flex flex-col gap-1">
                <DiskIORow
                    label="Disk Read"
                    bytesPerSec={diskIORate?.readBytesPerSec}
                    opsPerSec={diskIORate?.readOpsPerSec}
                />
                <DiskIORow
                    label="Disk Write"
                    bytesPerSec={diskIORate?.writeBytesPerSec}
                    opsPerSec={diskIORate?.writeOpsPerSec}
                />
            </div>

            <div className="border-t border-border-base pt-5">
                <StatRow label="Load average (1m / 5m / 15m)" tooltip="Average number of processes waiting to run over 1, 5, and 15 minute windows. Values above the core count indicate saturation.">
                    <span className="text-sm text-content-tertiary">{vm.cpu.cores} cores</span>
                    <span className="font-mono text-base text-content-primary">
                        {vm.load.avg1.toFixed(2)} / {vm.load.avg5.toFixed(2)} / {vm.load.avg15.toFixed(2)}
                    </span>
                </StatRow>
            </div>
        </div>
    );
};

const diskIOTooltips: Record<string, string> = {
    "Disk Read": "Data read from disk per second. IOPS = individual read operations per second.",
    "Disk Write": "Data written to disk per second. IOPS = individual write operations per second.",
};

const DiskIORow: FC<{ label: string; bytesPerSec: number | undefined; opsPerSec: number | undefined }> = ({
    label,
    bytesPerSec,
    opsPerSec,
}) => (
    <StatRow label={label} tooltip={diskIOTooltips[label]}>
        {bytesPerSec != null && opsPerSec != null ? (
            <>
                <span className="text-sm text-content-tertiary">{Math.round(opsPerSec)} iops</span>
                <span className="font-mono text-base text-content-primary">{formatBytes(bytesPerSec)}/s</span>
            </>
        ) : (
            <>
                <span className="text-sm text-content-tertiary">Refresh to update</span>
                <span className="font-mono text-base text-content-primary">N/A</span>
            </>
        )}
    </StatRow>
);

// --- Primitives ---

const StatRow: FC<{ label: string; tooltip?: string; children: ReactNode }> = ({ label, tooltip, children }) => (
    <div className="flex flex-wrap items-baseline justify-between gap-x-2">
        <div className="flex items-center gap-1">
            <Text className="text-base text-content-secondary">{label}</Text>
            {tooltip && (
                <Popover>
                    <PopoverTrigger asChild>
                        <button type="button" className="inline-flex cursor-pointer" style={{ marginTop: -1 }}>
                            <IconInfo size="sm" className="text-content-tertiary" aria-hidden />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-xs text-sm">{tooltip}</PopoverContent>
                </Popover>
            )}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-1.5">{children}</div>
    </div>
);

function barFillClass(percent: number): string {
    const color =
        percent > THRESHOLD_HIGH
            ? "bg-content-destructive"
            : percent > THRESHOLD_MEDIUM
              ? "bg-content-orange"
              : "bg-content-primary";
    return `h-full rounded-full transition-all duration-500 ${color}`;
}

const UsageBar: FC<{ label: string; percent: number; detail?: string }> = ({ label, percent, detail }) => {
    const clamped = Math.min(100, Math.max(0, percent));

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                <Text className="text-base text-content-secondary">{label}</Text>
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                    {detail && <span className="font-mono text-sm text-content-tertiary">{detail}</span>}
                    <span className="font-mono text-base text-content-primary">{clamped.toFixed(1)}%</span>
                </div>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-surface-tertiary">
                <div className={barFillClass(clamped)} style={{ width: `${clamped}%` }} />
            </div>
        </div>
    );
};
